import moment, {locale} from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Paragraph } from '../../../common/styled-components/typography'
import { useDateFormats } from '../../../hooks/useDateFormats'
import { getDisplayableAgeRestriction, getDisplayableSexRestriction, getDisplayableNumberOfPlacesRemaining, hasGroupPlacesLimited } from '../../../utils/qidigo'
import { getHtmlContent } from '../../../utils/text'
import { ICalendarGroup } from '../../calendar/types'
import GenderRestrictions from "../../GenderRestrictions";

const GroupInformationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const InfoBlockContainer = styled.div`
  flex-shrink: 0;
  margin-right: 10px;
  width: 120px;
  min-width: 100px;
  max-width: 20%;
  font-weight: bold;
`

const CompletedGroup = styled.span`
  color: red;
  text-transform: capitalize;
`

const StyledParagraph = styled(Paragraph)`
  text-align: justify;
  font-size: inherit;
`

interface GroupInformationCollapseViewProps {
  group: ICalendarGroup;
  withLocation?: boolean;
  withGroupDescription?: boolean;
}
const GroupInformationCollapseView: React.FC<GroupInformationCollapseViewProps> = ({ group, withLocation, withGroupDescription }) => {
  const { t } = useTranslation();
  const { shortFormat } = useDateFormats()

  const displayableDuration = group.start_date && (group.end_date
    ? t(
      'from$startDateTo$endDate',
      {
        startDate: moment(group.start_date).format(shortFormat),
        endDate: moment(group.end_date).format(shortFormat)
      }
    )
    : t('on$date', { date: moment(group.start_date).format(shortFormat) })
  );

  return (
    <>
      {
        withGroupDescription && (
          <StyledParagraph dangerouslySetInnerHTML={{ __html: getHtmlContent(group.description) }} />
        )
      }
      <GroupInformationContainer>
        <InfoBlockContainer>
          {t('scheduleShort')}
        </InfoBlockContainer>
        <div className={"schedule"}>
          {
            (withLocation && !!group.location) &&
            <div>
              {group.location.full_name}
            </div>
          }
          {
            displayableDuration
          }
          {
            group.schedules && group.schedules.map((x, i) =>
              <div key={`${x.type_day}-${x.start_time}-${x.end_time}-${i}`}>
                {t(x.type_day.toLowerCase())}
                {' '}
                {moment(x.start_time).format(locale() === 'en-ca' ? 'h:mm a' : 'HH[h]mm')}
                {' - '}
                {moment(x.end_time).format(locale() === 'en-ca' ? 'h:mm a' : 'HH[h]mm')}
              </div>
            )
          }
        </div>
      </GroupInformationContainer>
      {
        (hasGroupPlacesLimited(group)
          || group.restrictions_gender
          || group.restriction_age_min
          || group.restriction_age_max
        ) &&
        <GroupInformationContainer>
          <InfoBlockContainer>
            {t('restrictions.title')}
          </InfoBlockContainer>
          <div className={"restrictions"}>
            {
              hasGroupPlacesLimited(group) &&
              (
                group.nbr_places_remaining <= 0
                  ? <CompletedGroup>{t('complete')}</CompletedGroup>
                  : <div><b>{t('placesRemaining') + t('column')}</b> {getDisplayableNumberOfPlacesRemaining(t, group.nbr_places_remaining)}</div>
              )
            }
            {
              group.restrictions_gender &&
              <div className={'inline'}>
                <b>{t('gender') + t('column')}</b>
                <GenderRestrictions
                    genders={group.restrictions_gender}
                    translation={t}
                    extraInfoMessage={""}
                    forPassModal={true}
                />
              </div>
            }
            {
              (group.restriction_age_min || group.restriction_age_max) &&
              <div>
                <b>{t('age') + t('column')}</b>{getDisplayableAgeRestriction(t, group.restriction_age_min, group.restriction_age_max)}
              </div>
            }
          </div>
        </GroupInformationContainer>
      }
    </>
  )
};

export default GroupInformationCollapseView;
